import React, {useEffect, useState} from "react";
import {PencilSquareIcon, PlusIcon, TrashIcon,} from "@heroicons/react/24/outline";
import {Link} from "react-router-dom";
import {IoArrowBackSharp} from "react-icons/io5";

import axiosInstance from "../../utils/axiosInstance";
import Loader from "../../components/Loader";
import Notification from "../../components/Notification";

const AdditionalPrice = () => {
    const [additionalPrices, setAdditionalPrices] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [error, setError] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [priceToDelete, setPriceToDelete] = useState(null);
    const [showNotification, setShowNotification] = useState(false);

    useEffect(() => {
        fetchAdditionalPrices();
    }, []);

    const fetchAdditionalPrices = async () => {
        try {
            const response = await axiosInstance.get("/additional-prices");
            setAdditionalPrices(response.data);
        } catch (err) {
            setError("Failed to load additional prices.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (price) => {
        setPriceToDelete(price);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setButtonLoading(true);
            await axiosInstance.delete(
                `/additional-prices/${priceToDelete.id}`
            );
            setAdditionalPrices(
                additionalPrices.filter((p) => p.id !== priceToDelete.id)
            );
            setShowDeleteModal(false);
            setPriceToDelete(null);
            setShowNotification(true);
        } catch (err) {
            setError("Failed to delete additional price.");
        } finally {
            setButtonLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setPriceToDelete(null);
    };

    const filteredPrices = additionalPrices.filter(
        (item) =>
            item.column.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="container mx-auto p-6">
                <Loader/>
            </div>
        );
    }

    if (error) {
        return <p className="text-center text-red-500">{error}</p>;
    }

    return (
        <div className="container mx-auto p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                    <button
                        onClick={() => window.history.back()}
                        className="rounded-full hover:bg-gray-200 transition-all mr-2"
                    >
                        <IoArrowBackSharp size={24}/>
                    </button>
                    Additional Price List
                </h3>

                <Link
                    to="/admin/add-additional-price"
                    className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                >
                    <PlusIcon className="h-5 w-5 mr-2"/>
                    Add Additional Price
                </Link>
            </div>

            {/* Search */}
            <div className="mb-4">
                <input
                    type="text"
                    className="border border-gray-300 p-2 w-full rounded-md"
                    placeholder="Search by column or value..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Table */}
            <table className="min-w-full table-auto bg-white border border-gray-300 rounded-md">
                <thead>
                <tr className="bg-gray-100">
                    <th className="px-4 py-2 border-b text-left">Column</th>
                    <th className="px-4 py-2 border-b text-left">Value</th>
                    <th className="px-4 py-2 border-b text-left">Price</th>
                    <th className="px-4 py-2 border-b text-left">Rule</th>
                    <th className="px-4 py-2 border-b text-left">Actions</th>
                </tr>
                </thead>
                <tbody>
                {filteredPrices.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border-b">{item.column}</td>
                        <td className="px-4 py-2 border-b">{item.value}</td>
                        <td className="px-4 py-2 border-b">{item.credits}</td>
                        <td className="px-4 py-2 border-b">{item.rule}</td>
                        <td className="px-4 py-2 border-b flex">
                            <Link
                                to={`/admin/edit-additional-price/${item.id}`}
                                className="text-yellow-500 p-1 rounded mr-2 hover:bg-yellow-600 hover:text-white"
                            >
                                <PencilSquareIcon className="h-5 w-5"/>
                            </Link>
                            <button
                                className="text-red-500 p-1 rounded hover:bg-red-600 hover:text-white"
                                onClick={() => handleDeleteClick(item)}
                            >
                                <TrashIcon className="h-5 w-5"/>
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                        <h2 className="text-lg font-semibold mb-4">Are you sure?</h2>
                        <p>
                            Delete additional price for{" "}
                            <strong>{priceToDelete.value}</strong>?
                        </p>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                                onClick={handleDeleteConfirm}
                                disabled={buttonLoading}
                            >
                                {buttonLoading ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification */}
            {showNotification && (
                <Notification
                    setShowNotification={setShowNotification}
                    message="Additional price deleted successfully!"
                />
            )}
        </div>
    );
};

export default AdditionalPrice;